import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

import {
    motion,
    useAnimationFrame,
    useInView,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
    useVelocity
} from 'motion/react'

const wrap = (min: number, max: number, value: number): number => {
    const range = max - min

    return ((((value - min) % range) + range) % range) + min
}

interface MarqueeProps {
    children: ReactNode
    speed?: number
    className?: string
    trackClassName?: string
}

// An endless band that drifts on its own, accelerates with scroll speed and
// flips direction with scroll direction.
export const Marquee: FC<MarqueeProps> = ({ children, speed = 2.4, className, trackClassName }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref)
    const reduce = useReducedMotion()
    const baseX = useMotionValue(0)
    const { scrollY } = useScroll()
    const scrollVelocity = useVelocity(scrollY)
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 })
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], { clamp: false })
    const x = useTransform(baseX, value => `${wrap(-25, -50, value)}%`)
    const direction = useRef(1)

    useAnimationFrame((_, delta) => {
        if (reduce || !inView) return

        let moveBy = direction.current * -speed * (delta / 1000)
        const factor = velocityFactor.get()

        if (factor < 0) direction.current = -1
        else if (factor > 0) direction.current = 1

        moveBy += direction.current * moveBy * factor
        baseX.set(baseX.get() + moveBy)
    })

    return (
        <div ref={ref} className={cn('overflow-hidden', className)}>
            <motion.div className={cn('flex w-max flex-nowrap', trackClassName)} style={{ x: reduce ? '0%' : x }}>
                {[0, 1, 2, 3].map(copy => (
                    <div key={copy} className='flex shrink-0 flex-nowrap' aria-hidden={copy > 0}>
                        {children}
                    </div>
                ))}
            </motion.div>
        </div>
    )
}
